import { type PropsWithChildren, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { isClient } from "@/lib/common";
import { useProcessId } from "@/lib/hooks/use-process-id";
import {
    useShowRegistrationOnboardingContext,
    useUserAuthenticationContext,
    useViewportInfoContext,
} from "@/src/contexts";
import { usePageContext } from "@/src/contexts/PageContext";
import { useSearchNavigation } from "@/src/contexts/useSearchNavigation";
import type { IMaster } from "@/src/master/Master";
import MobileRegister from "@/src/master/MobileRegister";
import HashNavigation from "@/src/master/hash-navigation";
import { browserStatics } from "@/src/shared/BrowserStatic";
import MainLoading from "@Components/MainLoading";
import { getCookie, lazyLoadComponentFactory } from "@Components/Modules/Helpers";
import { hideMainLoading } from "@Components/Modules/MainLoading";
import ScrollTop from "@Components/scroll-top";
import * as stylex from "@stylexjs/stylex";

import Footer from "../shared/Footer";
import Header from "../shared/Header";
import { HeaderType } from "../shared/IHeader";
import useHeaders from "./Headers";

const SuccessRegistrationOnBoardingModal = lazyLoadComponentFactory(
    () => import(/* webpackChunkName: "registration-onboarding" */ "../modals/SuccessRegistrationOnBoarding"),
);

const PluginBottomBanner = lazyLoadComponentFactory(
    () => import(/* webpackChunkName: "plugin-bottom-banner" */ "../plugin/Banner/PluginBottomBanner"),
);

const Layout = (props: PropsWithChildren<IMaster>) => {
    const { isDesktop, isMobile } = useViewportInfoContext();
    const { isAuthenticated } = useUserAuthenticationContext();
    const { showRegistrationOnboarding, setShowRegistrationOnboarding } =
        useShowRegistrationOnboardingContext();
    const showSearchNavigation = useSearchNavigation();
    const { headerType, showFooter, pageTitle } = usePageContext();
    const { pathname } = useLocation();
    const { get } = useProcessId();

    useEffect(() => {
        const onPageShow = (event: PageTransitionEvent) => {
            const performanceCounter = performance?.getEntriesByType("navigation");
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const isBackForward = (performanceCounter?.[0] as any)?.type === "back_forward";
            if (event.persisted || isBackForward) {
                if (getCookie("language") !== browserStatics.culture) {
                    window.location.reload();
                    return;
                }

                hideMainLoading();
            }
        };

        window.addEventListener("pageshow", onPageShow);
        return () => {
            window.removeEventListener("pageshow", onPageShow);
        };
    }, [pathname]);

    const header = () => {
        switch (headerType) {
            case HeaderType.None:
                return null;
            default:
                return <Header {...props.header} />;
        }
    };

    useHeaders({ pageTitle });

    const showSuccessRegistrationOnBoarding =
        isAuthenticated && showRegistrationOnboarding && isClient && !get();

    return (
        <>
            <div {...stylex.props(layoutStyles.wrapper)}>
                {header()}
                <div {...stylex.props(layoutStyles.panel)}>
                    <Suspense fallback={<MainLoading {...stylex.props(layoutStyles.loading)} />}>
                        {props.children}
                        <HashNavigation />
                    </Suspense>
                </div>
                {showFooter && <Footer {...props.footer} />}
                <div {...stylex.props(layoutStyles.stickyToBottom)}>
                    {showFooter && <ScrollTop />}
                    {isDesktop && !showSearchNavigation && <PluginBottomBanner />}
                    {props.header && isMobile && !isAuthenticated && !showSearchNavigation && (
                        <MobileRegister />
                    )}
                </div>
            </div>
            {showSuccessRegistrationOnBoarding && (
                <SuccessRegistrationOnBoardingModal onClose={() => setShowRegistrationOnboarding(false)} />
            )}
        </>
    );
};

export const layoutStyles = stylex.create({
    wrapper: {
        minHeight: "100vh",
    },

    panel: {
        transform: "translateZ(0)",
        verticalAlign: "top",
    },

    loading: {
        position: "relative",
        height: "calc(40vh - 60px)",
    },

    stickyToBottom: {
        position: "sticky",

        zIndex: 60,
        bottom: 0,
    },
});

export default Layout;
